// @givmo/compliance — the recorded-parameter entry form (CCM-WEB1, surface 4).
//
// ══ RECORDING A FACT IS AN ACT WITH A NAME ON IT ═══════════════════════════════════════════════
//
// Every value this form writes is a fact the ORGANIZATION asserts about itself, attributed to the
// person who typed it and superseded rather than edited. So this is not "edit your settings": each
// save appends a new row, the previous one stays visible with its own attribution, and every
// candidate confirmed against the old value keeps resolving the row it was computed against.
//
// ⚠ NOTHING IS PRE-FILLED FROM A GUESS, AND NO BOOLEAN DEFAULTS. `undefined` is a real state here —
// it means the organization has not answered — and it is not the same as `false`. That matters most
// for "solicits nationwide or online", where a defaulted "no" would silently decide the size of the
// register, and a defaulted "yes" would propose obligations in dozens of jurisdictions nobody named.
//
// ⚠ MONEY IS A STRING ALL THE WAY TO THE WIRE. A revenue figure is a number the organization gave;
// parsing it to a float and back would make the register report a figure nobody stated.

import { useState } from 'react';
import { Button, Select, TextField, testIdProp } from '@givmo/design-system';
import {
  US_JURISDICTIONS,
  buildParameterValue,
  type ParameterInput,
  type ProfileParameterSpec,
} from './compliance-decode';

const PREFIX = 'compliance-facts';

/** The jurisdiction options every code/code-list input picks from. */
const JURISDICTION_OPTIONS = US_JURISDICTIONS.map((j) => ({ value: j.code, label: j.name }));

/**
 * One parameter's input, shaped by its value kind.
 *
 * Returns the typed wire value through `onSubmit`, or nothing at all while the input is not
 * answerable — a caller can never send a half-value.
 */
export function ParameterForm({
  spec,
  busy,
  onSubmit,
  onCancel,
}: {
  spec: ProfileParameterSpec;
  busy: boolean;
  onSubmit: (value: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [input, setInput] = useState<ParameterInput>({});

  function set(patch: Partial<ParameterInput>) {
    setInput((current) => ({ ...current, ...patch }));
  }

  const value = buildParameterValue(spec.kind, input);
  const ready = value !== undefined;

  return (
    <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-form-${spec.key}`)}>
      <p className="text-s13 font-w600 text-neutral-900">{spec.label}</p>
      <p className="text-s12 text-neutral-600">{spec.help}</p>

      {spec.kind === 'month_day' && (
        <div className="flex gap-8">
          <TextField
            type="number"
            label="Month"
            value={input.month ?? ''}
            onChange={(event) => set({ month: event.target.value })}
            testId={`${PREFIX}-input-month`}
          />
          <TextField
            type="number"
            label="Day"
            value={input.day ?? ''}
            onChange={(event) => set({ day: event.target.value })}
            testId={`${PREFIX}-input-day`}
          />
        </div>
      )}

      {spec.kind === 'jurisdiction_code' && (
        <Select
          label="Jurisdiction"
          options={JURISDICTION_OPTIONS}
          value={input.code ?? ''}
          onValueChange={(next) => set({ code: typeof next === 'string' ? next : '' })}
          testId={`${PREFIX}-input-jurisdiction`}
        />
      )}

      {spec.kind === 'jurisdiction_code_list' && (
        // A checkbox list rather than a multi-select: the set an organization names here decides
        // which jurisdictions get looked up at all, so it should be readable at a glance rather than
        // hidden behind a closed control.
        <fieldset className="flex flex-wrap gap-8" {...testIdProp(`${PREFIX}-input-jurisdictions`)}>
          <legend className="sr-only">{spec.label}</legend>
          {US_JURISDICTIONS.map((jurisdiction) => {
            const codes = input.codes ?? [];
            const checked = codes.includes(jurisdiction.code);
            return (
              <label
                key={jurisdiction.code}
                className="flex items-center gap-4 text-s12 text-neutral-700"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    set({
                      codes: checked
                        ? codes.filter((code) => code !== jurisdiction.code)
                        : [...codes, jurisdiction.code],
                    })
                  }
                  data-testid={`${PREFIX}-jurisdiction-${jurisdiction.code}`}
                />
                {jurisdiction.code}
              </label>
            );
          })}
        </fieldset>
      )}

      {spec.kind === 'money_usd' && (
        <TextField
          inputMode="decimal"
          label="Amount (USD)"
          value={input.amount ?? ''}
          onChange={(event) => set({ amount: event.target.value })}
          testId={`${PREFIX}-input-amount`}
        />
      )}

      {spec.kind === 'boolean' && (
        // ⚠ TWO EXPLICIT CHOICES, NEITHER PRE-SELECTED. A single toggle would ship an answer the
        // organization never gave, and on this key that answer changes the register.
        <div className="flex gap-8" {...testIdProp(`${PREFIX}-input-boolean`)}>
          <Button
            appearance="saas"
            tone={input.flag === true ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => set({ flag: true })}
            testId={`${PREFIX}-input-yes`}
          >
            Yes
          </Button>
          <Button
            appearance="saas"
            tone={input.flag === false ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => set({ flag: false })}
            testId={`${PREFIX}-input-no`}
          >
            No
          </Button>
        </div>
      )}

      {spec.kind === 'classification_code' && (
        <TextField
          label="Classification"
          value={input.code ?? ''}
          onChange={(event) => set({ code: event.target.value })}
          testId={`${PREFIX}-input-classification`}
        />
      )}

      <div className="flex gap-8">
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          disabled={!ready || busy}
          onClick={() => {
            if (value !== undefined) onSubmit(value);
          }}
          testId={`${PREFIX}-form-submit`}
        >
          Record this
        </Button>
        <Button
          appearance="saas"
          tone="ghost"
          size="sm"
          disabled={busy}
          onClick={onCancel}
          testId={`${PREFIX}-form-cancel`}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
